import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Box,
    Typography,
    FormControlLabel,
    CircularProgress,
    Divider,
    RadioGroup,
    Radio,
    FormControl,
} from '@mui/material';
import { AssessmentTypeEnum } from '../../../../Enum/AssessmentTypeEnum';
import { formatDate } from '../../../../utils/dateHelpers';

interface AssessmentStatus {
    type: AssessmentTypeEnum;
    title: string;
    hasChanges: boolean;
    hasExistingId: boolean;
    lastCompletedDate: string | null;
    isCompleted: boolean;
    action: 'complete' | 'agree' | 'not-reviewed' | null;
}

interface MultiAssessmentDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (assessmentActions: Record<AssessmentTypeEnum, 'complete' | 'agree' | 'not-reviewed' | null>) => void;
    isLoading?: boolean;
    assessments: {
        pastPresentHistory: { hasChanges: boolean; hasId: boolean; date: string | null; isCompleted: boolean };
        pastSurgicalHistory: { hasChanges: boolean; hasId: boolean; date: string | null; isCompleted: boolean };
        reviewSystems: { hasChanges: boolean; hasId: boolean; date: string | null; isCompleted: boolean };
        physicalExam: { hasChanges: boolean; hasId: boolean; date: string | null; isCompleted: boolean };
    };
}

const MultiAssessmentDialog: React.FC<MultiAssessmentDialogProps> = ({ open, onClose, onConfirm, isLoading = false, assessments }) => {
    const getAssessmentStatuses = (): AssessmentStatus[] => {
        return [
            {
                type: AssessmentTypeEnum.PastPresentHistory,
                title: 'Past/Present History',
                hasChanges: assessments.pastPresentHistory.hasChanges,
                hasExistingId: assessments.pastPresentHistory.hasId,
                lastCompletedDate: assessments.pastPresentHistory.date,
                isCompleted: assessments.pastPresentHistory.isCompleted,
                // Automatically set action to 'complete' if already completed and no changes
                action: assessments.pastPresentHistory.isCompleted && !assessments.pastPresentHistory.hasChanges ? 'complete' : null,
            },
            {
                type: AssessmentTypeEnum.PastSurgicalHistory,
                title: 'Past Surgical History',
                hasChanges: assessments.pastSurgicalHistory.hasChanges,
                hasExistingId: assessments.pastSurgicalHistory.hasId,
                lastCompletedDate: assessments.pastSurgicalHistory.date,
                isCompleted: assessments.pastSurgicalHistory.isCompleted,
                // Automatically set action to 'complete' if already completed and no changes
                action: assessments.pastSurgicalHistory.isCompleted && !assessments.pastSurgicalHistory.hasChanges ? 'complete' : null,
            },
            {
                type: AssessmentTypeEnum.ReviewSystems,
                title: 'Review of Systems',
                hasChanges: assessments.reviewSystems.hasChanges,
                hasExistingId: assessments.reviewSystems.hasId,
                lastCompletedDate: assessments.reviewSystems.date,
                isCompleted: assessments.reviewSystems.isCompleted,
                // Automatically set action to 'complete' if already completed and no changes
                action: assessments.reviewSystems.isCompleted && !assessments.reviewSystems.hasChanges ? 'complete' : null,
            },
            {
                type: AssessmentTypeEnum.PhysicalExam,
                title: 'Physical Examination',
                hasChanges: assessments.physicalExam.hasChanges,
                hasExistingId: assessments.physicalExam.hasId,
                lastCompletedDate: assessments.physicalExam.date,
                isCompleted: assessments.physicalExam.isCompleted,
                // Automatically set action to 'complete' if already completed and no changes
                action: assessments.physicalExam.isCompleted && !assessments.physicalExam.hasChanges ? 'complete' : null,
            },
        ];
    };

    const [assessmentStatuses, setAssessmentStatuses] = useState<AssessmentStatus[]>(getAssessmentStatuses());

    // Reset state when dialog opens with new data
    React.useEffect(() => {
        if (open) {
            setAssessmentStatuses(getAssessmentStatuses());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, assessments]);

    const handleActionChange = (type: AssessmentTypeEnum, action: 'complete' | 'agree' | 'not-reviewed' | null) => {
        setAssessmentStatuses((prev) => prev.map((status) => (status.type === type ? { ...status, action } : status)));
    };

    const handleConfirm = () => {
        const actions: Record<AssessmentTypeEnum, 'complete' | 'agree' | 'not-reviewed' | null> = Object.create(null);
        assessmentStatuses.forEach((status) => {
            actions[status.type] = status.action;
        });
        onConfirm(actions);
    };

    const canProceed = () => {
        // All assessments must have an action selected
        return assessmentStatuses.every((status) => status.action !== null);
    };

    const renderAssessmentRow = (status: AssessmentStatus) => {
        const requiresCompletion = status.hasChanges;
        const hasLastCompleted = Boolean(status.lastCompletedDate);
        const isAlreadyCompleted = status.isCompleted && !status.hasChanges;

        return (
            <Box key={status.type} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {status.title}
                </Typography>

                {/* Status information */}
                <Box sx={{ ml: 2, mb: 1 }}>
                    {isAlreadyCompleted && (
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                            ✓ Assessment already completed
                        </Typography>
                    )}
                    {hasLastCompleted && !isAlreadyCompleted && (
                        <Typography variant="body2" color="text.secondary">
                            Last completed: {formatDate(status.lastCompletedDate, 'MM/dd/yyyy')}
                        </Typography>
                    )}
                    {!status.hasChanges && !status.hasExistingId && !hasLastCompleted && !isAlreadyCompleted && (
                        <Typography variant="body2" color="text.secondary">
                            Not previously completed
                        </Typography>
                    )}
                </Box>

                {/* Action selection */}
                {!isAlreadyCompleted && (
                    <FormControl component="fieldset" sx={{ ml: 2 }}>
                        <RadioGroup
                            value={status.action || ''}
                            onChange={(e) => handleActionChange(status.type, e.target.value as 'complete' | 'agree' | 'not-reviewed')}
                        >
                            {requiresCompletion ? (
                                // Show complete option for assessments with changes
                                <FormControlLabel
                                    value="complete"
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography variant="body2">
                                            <strong>Confirm and acknowledge completion</strong>
                                        </Typography>
                                    }
                                />
                            ) : hasLastCompleted ? (
                                // Show agree/not-reviewed for assessments with no changes but have last completed date
                                <>
                                    <FormControlLabel
                                        value="agree"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="body2">Agree with last assessment</Typography>}
                                    />
                                    <FormControlLabel
                                        value="not-reviewed"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="body2">Assessment not reviewed</Typography>}
                                    />
                                </>
                            ) : (
                                // For assessments never completed, only show not-reviewed option
                                <FormControlLabel
                                    value="not-reviewed"
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">Assessment not reviewed</Typography>}
                                />
                            )}
                        </RadioGroup>
                    </FormControl>
                )}

                <Divider sx={{ mt: 2 }} />
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">Physical Health Assessment Review</Typography>
                    {isLoading && <CircularProgress size={20} />}
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Please review and confirm the status of each assessment before proceeding to the next section.
                </Typography>

                {assessmentStatuses.map((status) => renderAssessmentRow(status))}
            </DialogContent>

            <DialogActions>
                <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'center', pb: 1 }}>
                    <Button onClick={onClose} variant="outlined" disabled={isLoading} sx={{ textTransform: 'none' }}>
                        Go Back
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={!canProceed() || isLoading}
                        sx={{ textTransform: 'none' }}
                        endIcon={isLoading ? <CircularProgress size={18} /> : null}
                    >
                        Confirm & Continue
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default MultiAssessmentDialog;
